/**
 * @license Siftrock
 * (c) 2014-2018 Siftrock, Inc. http://siftrock.com
 * Unauthorized copying of this file, via any medium is strictly prohibited
 */

import { Component, OnInit } from '@angular/core';
import {style, animate, transition, state, trigger} from '@angular/animations';

import { ActivatedRoute } from '@angular/router';

import { CacheService } from '../../global/ng2-cache/services/cache.service';
import { SessionService } from '../../services/session2.service';
import { ErrorService } from '../../services/error.service';
import { WorkflowService } from '../../services/workflow.service';
import { GlobalMessageService } from '../../services/global-message.service';
import { DriftService } from '../../services/drift.service';

import { Constants, EmailType, Language, ContactQuality,  SiftrockField } from '../../global/constants';

@Component({
  selector: 'app-alert-wf-detail',
  templateUrl: './alert-wf-detail.component.html',
  animations: [
    trigger('draftState',
      [
        state('inactive', style({
          opacity: 0,
          transform: 'scale(.9)'
        })),
        state('active', style({
          opacity: 1,
          transform: 'scale(1)'
        })),
        transition('inactive => active', animate('300ms ease-in'))
      ]
    )
  ]
})
export class AlertWfDetailComponent implements OnInit {

  loadingPage = true;
  id: string = '';
  partner: string ='';
  activeStep: string = 'type';
  help: boolean = false;
  workflow: any = {};
  draftSaved: string = '';
  draftState: string = 'inactive';

  // type
  selectedTypes: any = {};
  selectedLanguages: any[] = [];
  languageList: any[] = [];
  languageDDSettings: any = {
    text: 'Select language(s)',
    unSelectAllText: 'Remove all but English'
  };

  // routing/state management
  currentStaticEmail: string = '';
  currentDynamicDomain: string = '';

  // filter/state management
  currentIncludeFilterField: SiftrockField;
  includeFilterValue: string = '';
  currentExcludeFilterField: SiftrockField;
  excludeFilterValue: string = '';

  constructor(private route: ActivatedRoute,
              private cacheService: CacheService,
              private sessionService: SessionService,
              private errorService: ErrorService,
              private workflowService: WorkflowService,
              private globalMessageService: GlobalMessageService,
              private driftService: DriftService,
              private constants: Constants) { }

  ngOnInit() {
    this.constants.emailTypes.forEach((t) => {
      this.selectedTypes[t.typeName] = false;
    });

    this.constants.languages.forEach(l => {
      if (l.supported) {
        this.languageList.push({
          id: l.name,
          itemName: l.display
        });
      }
    });

    this.languageList.push({
      id: 'other',
      itemName: 'Other'
    });

    if (!this.cacheService.get('session')) {
      this.sessionService.getSession(true).subscribe(
        (session: any) => {
          this.initView();
        }
      )
    }
    else {
      this.initView();
    }
  }

  typeChange() {
    this.workflow.trigger.type = [];
    for (var key in this.selectedTypes) {
      if (this.selectedTypes[key]) {
        this.workflow.trigger.type.push(key);
      }
    }

    var update = { 'trigger.type': this.workflow.trigger.type };
    this.saveWorkflow(update);
    this.driftService.track('edited-workflow', { type: `Route email`, field: 'Email type' });
  }

  languageAdd(item) {
    if (this.selectedLanguages.length == this.languageList.length) {
      // 'all' languages
      this.workflow.trigger.language = ['all'];
    }
    else {
      this.workflow.trigger.language = this.selectedLanguages.map(l => { return l.id });
    }

    var update = { 'trigger.language': this.workflow.trigger.language };
    this.saveWorkflow(update);
    this.driftService.track('edited-workflow', { type: `Route email`, field: 'Language' });
  }

  languageRemove(item) {
    if (this.selectedLanguages.length > 0) {
      this.workflow.trigger.language = this.selectedLanguages.map(l => { return l.id });
    }
    else {
      // force at least one language
      this.selectedLanguages = [{ id: 'en', itemName: 'English'}];
      this.workflow.trigger.language = ['en'];
    }
    var update = { 'trigger.language': this.workflow.trigger.language };
    this.saveWorkflow(update);
    this.driftService.track('edited-workflow', { type: `Route email`, field: 'Language' });
  }

  languageAddAll(items) {
    this.workflow.trigger.language = ['all'];
    var update = { 'trigger.language': ['all'] };
    this.saveWorkflow(update);
    this.driftService.track('edited-workflow', { type: `Route email`, field: 'Language' });
  }

  languageRemoveAll(items) {
    // force at least one language
    this.selectedLanguages = [{ id: 'en', itemName: 'English'}];
    this.workflow.trigger.language = ['en'];
    var update = { 'trigger.language': this.workflow.trigger.language };
    this.saveWorkflow(update);
    this.driftService.track('edited-workflow', { type: `Route email`, field: 'Language' });
  }

  addStaticEmail() {
    this.workflow.action.forwards.push(this.currentStaticEmail.toLowerCase());
    this.workflow.action.forwards.sort();
    this.currentStaticEmail = '';
    this.saveWorkflow( {'action.forwards': this.workflow.action.forwards} );
    this.driftService.track('edited-workflow', { type: `Route email`, field: 'Static address' });
  }

  removeStaticEmail(email) {
    this.workflow.action.forwards.splice(this.workflow.action.forwards.indexOf(email), 1);
    this.saveWorkflow( {'action.forwards': this.workflow.action.forwards} );
    this.driftService.track('edited-workflow', { type: `Route email`, field: 'Static address' });
  }

  addDynamicDomain() {
    this.workflow.action.domain = this.currentDynamicDomain;
    this.currentDynamicDomain = '';
    this.saveWorkflow( {'action.domain': this.workflow.action.domain} );
    this.driftService.track('edited-workflow', { type: `Route email`, field: 'Dynamic domain' });
  }

  removeDynamicDomain() {
    this.workflow.action.domain = '';
    this.saveWorkflow( {'action.domain': ''} );
    this.driftService.track('edited-workflow', { type: `Route email`, field: 'Dynamic domain' });
  }

  addIncludeFilter() {
    this.includeFilterValue = this.includeFilterValue.toLowerCase();
    var found = false;
    this.workflow.action.include_filters.forEach((f) => {
      if (f.field == this.currentIncludeFilterField.fieldName) {
        found = true;
        f.values.push(this.includeFilterValue);
      }
    });

    if (!found) {
      this.workflow.action.include_filters.push({
        field: this.currentIncludeFilterField.fieldName,
        display: this.currentIncludeFilterField.display,
        values: [this.includeFilterValue]
      });
    }

    this.workflow.action.include_filters.sort((a,b) => {
      if (a.display < b.display) {
        return -1;
      }
      else {
        return 1;
      }
    });

    this.includeFilterValue = '';
    this.saveWorkflow( {'action.include_filters': this.workflow.action.include_filters} );
    this.driftService.track('edited-workflow', { type: `Route email`, field: 'Include filter' });
  }

  removeIncludeFilter(filter, value) {
    filter.values.splice(filter.values.indexOf(value), 1);
    if (filter.values.length == 0) {
      this.workflow.action.include_filters.splice(this.workflow.action.include_filters.indexOf(filter), 1);
    }
    this.saveWorkflow( {'action.include_filters': this.workflow.action.include_filters} )
    this.driftService.track('edited-workflow', { type: `Route email`, field: 'Include filter' });
  }

  addExcludeFilter() {
    this.excludeFilterValue = this.excludeFilterValue.toLowerCase();
    var found = false;
    this.workflow.action.exclude_filters.forEach((f) => {
      if (f.field == this.currentExcludeFilterField.fieldName) {
        found = true;
        f.values.push(this.excludeFilterValue);
      }
    });

    if (!found) {
      this.workflow.action.exclude_filters.push({
        field: this.currentExcludeFilterField.fieldName,
        display: this.currentExcludeFilterField.display,
        values: [this.excludeFilterValue]
      });
    }

    this.workflow.action.exclude_filters.sort((a,b) => {
      if (a.display < b.display) {
        return -1;
      }
      else {
        return 1;
      }
    });

    this.excludeFilterValue = '';
    this.saveWorkflow( {'action.exclude_filters': this.workflow.action.exclude_filters} );
    this.driftService.track('edited-workflow', { type: `Route email`, field: 'Exclude filter' });
  }

  removeExcludeFilter(filter, value) {
    filter.values.splice(filter.values.indexOf(value), 1);
    if (filter.values.length == 0) {
      this.workflow.action.exclude_filters.splice(this.workflow.action.exclude_filters.indexOf(filter), 1);
    }
    this.saveWorkflow( {'action.exclude_filters': this.workflow.action.exclude_filters} )
    this.driftService.track('edited-workflow', { type: `Route email`, field: 'Exclude filter' });
  }

  private initView() {
    this.route.params.subscribe(
      params => {
        this.id = params['id'];
        if (this.id == 'new') {
          // check query string for type
          this.route.queryParams.subscribe(
            qs => {
              this.initNew();
              this.loadingPage = false;
            }
          );
        }
        else {
          this.workflowService.getWorkflow(this.id).subscribe(
            (result: any) => {
              this.workflow = result;
              this.workflow.trigger.type.forEach((t) => {
                this.selectedTypes[t] = true;
              });

              this.workflow.trigger.language.forEach(lang => {
                this.languageList.forEach(listlang => {
                  if (listlang.id == lang || lang == 'all') {
                    this.selectedLanguages.push(listlang);
                  }
                });
              });

              this.loadingPage = false;
            },
            error => {
              this.globalMessageService.error();
              this.errorService.log(error, 'alert-wf-detail.component', 'ngOnInit.100');
            }
          )
        }
      }
    )
  }

  private saveWorkflow(update: any) {
    this.draftState = 'inactive';
    if (this.workflow._id == 'new') {
      // special case for first-time save
      this.workflowService.saveNew(this.workflow).subscribe(
        (result: any) => {
          this.workflow = result;
          this.draftSaved = new Date().toLocaleTimeString();
          this.draftState = 'active';
          this.driftService.track('added-workflow', { type: 'Route email' });
        },
        error => {
          this.globalMessageService.error();
          this.errorService.log(error, 'alert-wf-detail.component', 'saveWorkflow.100');
        }
      )
    }
    else {
      this.workflowService.savePartial(this.workflow._id, update).subscribe(
        (result: any) => {
          this.draftSaved = new Date().toLocaleTimeString();
          this.draftState = 'active';
        },
        error => {
          this.globalMessageService.error();
          this.errorService.log(error, 'alert-wf-detail.component', 'saveWorkflow.200');
        }
      )
    }
  }

  private initNew() {
    this.selectedTypes['human'] = true;

    // Default is all languages
    this.selectedLanguages = JSON.parse(JSON.stringify(this.languageList));

    this.workflow = {
      _id: 'new',
      type: 'alert',
      partner: 'siftrock',
      trigger: {
          type: ['human'],
          language: ['all']
      },
      action: {
          domain: '',
          forwards: [],
          include_filters: [],
          exclude_filters: []
      },
      enabled: false,
      deleted: false
    };

    this.updateSession();
  }

  private updateSession() {
    // when a user creates a new workflow, re-set their session to whatever type they are creating
    // so when navigating back to workflows the created workflow will be on the screen
    this.sessionService.getSession().subscribe(
      (result: any) => {
        if (result.workflow_filters) {
          result.workflow_filters.type = 'all';
          result.workflow_filters.wf_type = 'alert';
          result.workflow_filters.last_changed = new Date();
          this.sessionService.saveSession(result).subscribe(
            (result: any) => {
              // Nothing
            },
            err => {
              this.errorService.log(err, 'alert-wf-detail.component', 'saveSession');
            }
          )
        }
      },
      error => {

      }
    )
  }
}



// WEBPACK FOOTER //
// ./client/app/config/workflows/alert-wf-detail.component.ts